/* The small labelled markers that sit under a row: why a question is in the queue, what a
 * system's key looks like, whether an account is disabled. Every one carries a word, never a
 * colour alone.
 */

import { useState, type ReactNode } from 'react';
import { Badge } from '@nextleap-al/admin-ui';

export type SignalTone = 'plain' | 'good' | 'warning' | 'critical';

const VARIANT = {
  plain: 'default',
  good: 'success',
  warning: 'warning',
  critical: 'error',
} as const;

export function Signal({ tone = 'plain', children }: { tone?: SignalTone; children: ReactNode }) {
  return (
    <Badge variant={VARIANT[tone]} size="sm">
      {children}
    </Badge>
  );
}

/** A row of signals, wrapping on a narrow screen. */
export function Signals({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap items-center gap-1.5">{children}</div>;
}

/** The heading of an expandable row: the thing itself, then its context in muted text. */
export function RowHead({ title, meta }: { title: ReactNode; meta?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
      <span className="font-medium text-[var(--text-primary)]">{title}</span>
      {meta && <span className="text-sm text-[var(--text-muted)]">{meta}</span>}
    </div>
  );
}

/**
 * One entry in a list of expandable rows. The body is mounted only once the row is opened,
 * so forty closed rows do not fetch forty details nobody has asked for.
 */
export function ExpandableRow({
  head,
  signals,
  summary,
  children,
  note,
}: {
  head: ReactNode;
  signals?: ReactNode;
  /** The wording on the disclosure control. */
  summary: string;
  children: ReactNode;
  note?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-[var(--radius-md)] border border-[var(--border-light)] bg-[var(--bg-elevated)] p-4">
      {head}
      {signals && <div className="mt-2">{signals}</div>}
      {note && <p className="mt-2 text-sm text-[var(--text-secondary)]">{note}</p>}
      <details
        className="mt-3"
        onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}
      >
        <summary className="cursor-pointer text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
          {summary}
        </summary>
        <div className="mt-4 border-t border-[var(--border-light)] pt-4">{open && children}</div>
      </details>
    </div>
  );
}

export function Rows({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-3">{children}</div>;
}
